import React from "react";
import { Pencil, Trash2 } from "lucide-react";
import { Popconfirm } from "antd";
import { roleDisplayTier } from "../../utils/orgChartModel";

/** Border + accent for org cards by role */
const CARD_ACCENT = {
  LEAD_ADVISOR: { border: "border-[color:var(--color-accent)]", designation: "text-white/80", dot: "bg-[color:var(--color-accent)]", badge: "bg-[color:var(--color-accent-fill)] text-white border-[color:var(--color-accent)]" },
  COMPANY_SPOC: { border: "border-[color:var(--color-ok-fg)]", designation: "text-[color:var(--color-ok-fg)]", dot: "bg-[color:var(--color-ok-bg)]", badge: "bg-[color:var(--color-ok-bg)] text-[color:var(--color-ok-fg)] border-[color:var(--color-ok-fg)]" },
  COMPANY_DELEGATE: { border: "border-[color:var(--color-border-strong)]", designation: "text-white/80", dot: "bg-[color:var(--color-text-muted-dark)]", badge: "bg-[color:var(--color-text-muted-dark)] text-white border-[color:var(--color-border-strong)]" },
  ADVISOR: { border: "border-[color:var(--color-accent)]", designation: "text-white/80", dot: "bg-[color:var(--color-accent)]", badge: "bg-[color:var(--color-accent-fill)] text-white border-[color:var(--color-accent)]" },
  UNKNOWN: { border: "border-[color:var(--color-border-strong)]", designation: "text-[color:var(--color-text-muted-dark)]", dot: "bg-[color:var(--color-text-muted)]", badge: "bg-[color:var(--color-text-muted)] text-white border-[color:var(--color-border-strong)]" },
};

function roleLabel(token) {
  return String(token || "UNKNOWN").replace(/_/g, " ");
}

function OrgCard({ row, onEdit, onDelete, disabled, readOnly }) {
  const acc = CARD_ACCENT[row.roleNormalized] || CARD_ACCENT.UNKNOWN;
  return (
    <div
      className={`group relative w-[200px] rounded-2xl border-2 ${acc.border} bg-gradient-to-b from-[color:var(--color-bg-white)] to-[color:var(--color-surface-sunken)] px-3 py-3 text-center shadow-sm transition-shadow hover:shadow-md`}
    >
      <div className={`absolute left-1/2 top-0 h-2 w-2 -translate-x-1/2 -translate-y-1/2 rounded-full ${acc.dot}`} aria-hidden />
      {!readOnly && (
        <div className="absolute right-1 top-1 flex gap-0.5 opacity-0 transition-opacity group-hover:opacity-100">
          <button
            type="button"
            disabled={disabled}
            onClick={() => onEdit?.(row)}
            className="rounded-md p-1 text-[color:var(--color-text-muted)] hover:bg-[color:var(--color-bg-alt)] hover:text-[color:var(--color-text-hero)] disabled:opacity-40"
            aria-label="Edit person"
          >
            <Pencil className="h-3.5 w-3.5" />
          </button>
          <Popconfirm title="Remove this person?" description="Direct reports will move to top level." okText="Remove" cancelText="Cancel" onConfirm={() => onDelete?.(row)} disabled={disabled}>
            <button
              type="button"
              disabled={disabled}
              className="rounded-md p-1 text-[color:var(--color-danger-fg)] hover:bg-[color:var(--color-danger-bg)] disabled:opacity-40"
              aria-label="Delete person"
            >
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          </Popconfirm>
        </div>
      )}
      <p className={`text-sm font-bold text-[color:var(--color-text-hero)] ${readOnly ? "" : "pr-6"}`}>{row.name}</p>
      <p className={`mt-0.5 text-xs font-semibold ${acc.designation}`}>{row.designation || "-"}</p>
      <span
        className={`mt-2 inline-block rounded-full border px-2 py-0.5 text-[8px] font-black uppercase tracking-widest ${acc.badge}`}
      >
        {roleLabel(row.roleNormalized)}
      </span>
      <p className="mt-2 text-[10px] font-medium text-[color:var(--color-text-muted)]">{row.department || "-"}</p>
    </div>
  );
}

/** Group direct reports into strata: lead advisors, then SPOC/advisor, then delegates. */
function partitionChildrenByDisplayTier(children) {
  const buckets = [[], [], []];
  for (const ch of children) {
    const t = roleDisplayTier(ch.row.roleNormalized);
    const b = Math.min(Math.max(t, 0), 2);
    buckets[b].push(ch);
  }
  buckets.forEach((b) => b.sort((a, c) => (a.row.index ?? 0) - (c.row.index ?? 0)));
  return buckets.map((nodes, tier) => ({ tier, nodes })).filter((x) => x.nodes.length > 0);
}

function TierRow({ nodes, onEdit, onDelete, disabled, readOnly }) {
  return (
    <div className="relative flex flex-row flex-wrap items-start justify-center gap-x-8 gap-y-6 pt-0">
      {nodes.length > 1 && (
        <div
          className="absolute left-[12%] right-[12%] top-0 hidden h-px bg-[color:var(--color-border-strong)] md:block"
          style={{ minWidth: "40px" }}
          aria-hidden
        />
      )}
      {nodes.map((ch) => (
        <div key={ch.row.id} className="relative flex flex-col items-center pt-1">
          <div className="mb-1 h-4 w-px shrink-0 bg-[color:var(--color-border-strong)] md:block" />
          <Subtree node={ch} onEdit={onEdit} onDelete={onDelete} disabled={disabled} readOnly={readOnly} />
        </div>
      ))}
    </div>
  );
}

function Subtree({ node, onEdit, onDelete, disabled, readOnly }) {
  const { row, children } = node;
  if (!children?.length) {
    return (
      <div className="flex flex-col items-center">
        <OrgCard row={row} onEdit={onEdit} onDelete={onDelete} disabled={disabled} readOnly={readOnly} />
      </div>
    );
  }

  const tierBlocks = partitionChildrenByDisplayTier(children);

  return (
    <div className="flex flex-col items-center">
      <OrgCard row={row} onEdit={onEdit} onDelete={onDelete} disabled={disabled} readOnly={readOnly} />
      <div className="flex w-full flex-col items-center">
        <div className="h-5 w-px shrink-0 bg-[color:var(--color-border-strong)]" />
        {tierBlocks.map((block, blockIdx) => (
          <React.Fragment key={block.tier}>
            {blockIdx > 0 && <div className="h-5 w-px shrink-0 bg-[color:var(--color-border-strong)]" aria-hidden />}
            <TierRow nodes={block.nodes} onEdit={onEdit} onDelete={onDelete} disabled={disabled} readOnly={readOnly} />
          </React.Fragment>
        ))}
      </div>
    </div>
  );
}

/**
 * Company logo header for the org-chart canvas.
 * Commandment VIII (Branding Propagates End-to-End): the client company's logo — resolved
 * upstream via the same `durableBlobUrlIfAzure(attrValue(attrs, "companyLogo"))` path used
 * for the Artefacts-tab branding preview — must appear on this canvas too, not just on
 * generated-artefact previews elsewhere in the app.
 */
function ChartLogoHeader({ logoUrl }) {
  if (!logoUrl) return null;
  return (
    <div className="mb-4 flex justify-end pr-2">
      <img
        src={logoUrl}
        alt="Company logo"
        className="h-10 max-w-[160px] object-contain"
        onError={(e) => {
          e.currentTarget.style.display = "none";
        }}
      />
    </div>
  );
}

/**
 * @param {object} props
 * @param {Array<{ row: object, children: object[] }>} props.forest
 * @param {(row: object) => void} props.onEdit
 * @param {(row: object) => void} props.onDelete
 * @param {boolean} [props.disabled]
 * @param {boolean} [props.readOnly] hide edit/delete on cards
 * @param {string} [props.logoUrl] resolved company logo URL (Commandment VIII); omitted renders no header
 */
export function OrgChartCanvas({ forest, onEdit, onDelete, disabled, readOnly, logoUrl }) {
  if (!forest?.length) {
    return (
      <div>
        <ChartLogoHeader logoUrl={logoUrl} />
        <div className="rounded-xl border border-dashed border-[color:var(--color-border)] bg-[color:var(--color-surface-sunken)] py-16 text-center text-sm font-medium text-[color:var(--color-text-muted)]">
          No people in this chart yet.
        </div>
      </div>
    );
  }

  if (forest.length === 1) {
    return (
      <div className="pb-6 pt-2">
        <ChartLogoHeader logoUrl={logoUrl} />
        <div className="overflow-x-auto">
          <div className="flex min-w-max justify-center px-4">
            <Subtree node={forest[0]} onEdit={onEdit} onDelete={onDelete} disabled={disabled} readOnly={readOnly} />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="pb-6 pt-2">
      <ChartLogoHeader logoUrl={logoUrl} />
      <div className="overflow-x-auto">
        <div className="flex min-w-max flex-row flex-wrap items-start justify-center gap-12 px-4">
          {forest.map((node) => (
            <Subtree key={node.row.id} node={node} onEdit={onEdit} onDelete={onDelete} disabled={disabled} readOnly={readOnly} />
          ))}
        </div>
      </div>
    </div>
  );
}
